/**
 * The drag-and-drop list (piece 4): one module for the New View Modal's column
 * picker and Tile Canvas's edit mode. Keyboard reorder is not optional: Space
 * grabs the focused item, the arrows move it, Space drops it and Escape
 * cancels, so anyone who cannot drag can still reorder.
 */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  if (from < 0 || from >= items.length) return [...items];
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(to, next.length)), 0, item!);
  return next;
}

export interface DragListState<T> {
  items: T[];
  /** The index of the item the keyboard has picked up, or null. */
  grabbed: number | null;
  /** The order before the grab, restored on Escape. */
  before: T[] | null;
}

export type DragListKey = ' ' | 'Enter' | 'ArrowUp' | 'ArrowDown' | 'Escape';

export function dragListKey<T>(state: DragListState<T>, key: DragListKey, focusIndex: number): { state: DragListState<T>; focusIndex: number } {
  switch (key) {
    case ' ':
    case 'Enter':
      if (state.grabbed === null) return { state: { ...state, grabbed: focusIndex, before: [...state.items] }, focusIndex };
      return { state: { ...state, grabbed: null, before: null }, focusIndex };
    case 'Escape':
      if (state.grabbed === null) return { state, focusIndex };
      return { state: { items: state.before ?? state.items, grabbed: null, before: null }, focusIndex: state.before ? state.items.indexOf(state.items[state.grabbed]!) : focusIndex };
    case 'ArrowUp':
    case 'ArrowDown': {
      const delta = key === 'ArrowUp' ? -1 : 1;
      const target = Math.max(0, Math.min(state.items.length - 1, focusIndex + delta));
      if (state.grabbed === null) return { state, focusIndex: target };
      if (target === state.grabbed) return { state, focusIndex: target };
      return { state: { ...state, items: moveItem(state.items, state.grabbed, target), grabbed: target }, focusIndex: target };
    }
  }
}

/** A pointer drop: the same move the keyboard makes. */
export function dropItem<T>(state: DragListState<T>, from: number, to: number): DragListState<T> {
  return { items: moveItem(state.items, from, to), grabbed: null, before: null };
}
